import { useCurrentId } from "hooks/useCurrentId.ts"
import { ConfigurationCustomize } from "pages/ConfigurationPage/ConfigurationCustomize"
import { ConfigurationFeatures } from "pages/ConfigurationPage/ConfigurationFeatures"
import { ConfigurationSdkToken } from "pages/ConfigurationPage/ConfigurationSdkToken"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { typedEnv } from "../../config/typedEnv"
import { ArrowLeft, Check, Eye, Loader2 } from "lucide-react"
import { useMerchantQuery, useSaveMerchantMutation } from "hooks/query/useMerchantQuery.ts"
import PageLoader from "components/PageLoader"
import { useBrandsQuery } from "hooks/query/useBrandsQuery.ts"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { Link } from "react-router-dom"

const tabsTemplate = (t: (key: string) => string) => [
  {
    title: t("configurationPage.features"),
    key: "features",
    Component: ConfigurationFeatures,
  },
  {
    title: t("configurationPage.sdkToken"),
    key: "sdkToken",
    Component: ConfigurationSdkToken,
  },
  {
    title: t("configurationPage.customize"),
    key: "customize",
    Component: ConfigurationCustomize,
  },
]

interface ConfigurationData {
  config: Record<string, unknown>
  sdkToken: string
}

export const ConfigurationPage = () => {
  const { t } = useTranslation()
  const [currentTab, setCurrentTab] = useState("features")
  const [data, setData] = useState<ConfigurationData | null>(null)
  const [validData, setValidData] = useState<Record<string, boolean>>({})
  const { isLoading: brandsAreLoading } = useBrandsQuery()
  const merchantId = useCurrentId()

  const { data: merchant, isLoading: merchantLoading } = useMerchantQuery(merchantId)
  const { mutate: saveMerchant, isPending: saveLoading } = useSaveMerchantMutation(merchantId)

  useEffect(() => {
    if (merchant?.token) {
      const { config, sdkToken } = JSON.parse(merchant.token)
      setData({ config: JSON.parse(config), sdkToken })
      setValidData(tabsTemplate(t).reduce((acc, { key }) => ({ ...acc, [key]: true }), {}))
    }
  }, [merchant, t])

  const valid = Object.values(validData).every((value) => value)

  const onSave = () => {
    if (!merchant || !data) return
    saveMerchant({
      ...merchant,
      token: JSON.stringify({
        config: JSON.stringify(data.config),
        sdkToken: data.sdkToken,
      }),
    })
  }

  return (
    <div data-testid="configuration-page">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="flex h-14 items-center justify-between px-6">
          <div className="flex items-center gap-4">
            <Link
              to="/merchants"
              className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <ArrowLeft className="size-4" />
              {t("configurationPage.backToMerchants")}
            </Link>
            <div className="h-4 w-px bg-border" />
            <h1 className="text-lg font-semibold" data-testid="configuration-title">
              {`${t("configurationPage.merchantConfiguration")} "${merchant?.name || ""}"`}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" asChild>
              <a
                href={`https://${typedEnv.VITE_TINT_WIDGET_LINK}/?merchantId=${merchantId}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="configuration-preview"
              >
                <Eye className="mr-1.5 size-3.5" />
                {t("previewTintVto")}
              </a>
            </Button>
            <Button
              size="sm"
              onClick={onSave}
              disabled={!valid || saveLoading}
              data-testid="configuration-save"
            >
              {saveLoading ? (
                <Loader2 className="mr-1.5 size-3.5 animate-spin" />
              ) : (
                <Check className="mr-1.5 size-3.5" />
              )}
              {t("configurationPage.save")}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {merchantLoading || brandsAreLoading || !data ? (
          <PageLoader />
        ) : (
          <Tabs value={currentTab} onValueChange={setCurrentTab}>
            <TabsList className="mb-6" data-testid="configuration-tabs">
              {tabsTemplate(t).map(({ title, key }) => (
                <TabsTrigger
                  key={key}
                  value={key}
                  className={!validData[key] ? "text-destructive" : ""}
                  data-testid={`configuration-tab-${key}`}
                >
                  {title}
                </TabsTrigger>
              ))}
            </TabsList>

            {tabsTemplate(t).map(({ Component, key }) => (
              <TabsContent key={key} value={key} data-testid={`configuration-tab-panel-${key}`}>
                <Component
                  data={data}
                  onChange={setData}
                  onValid={(valid: boolean) => setValidData((prev) => ({ ...prev, [key]: valid }))}
                />
              </TabsContent>
            ))}
          </Tabs>
        )}
      </div>
    </div>
  )
}
